import CheckCircle from "@redotech/redo-icons/check-circle.svg";
import LinkExternal2 from "@redotech/redo-icons/link-external-02.svg";
import RefreshCcw from "@redotech/redo-icons/refresh-ccw-01_filled.svg";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoLinkInput } from "@redotech/redo-web/arbiter-components/input/redo-link-input";
import {
  RedoTextareaInputField,
  RedoTextareaInputFieldSize,
} from "@redotech/redo-web/arbiter-components/input/redo-textarea-input-field";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { RedoRadioGroup } from "@redotech/redo-web/arbiter-components/radio/redo-radio-group";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Flowchart } from "@redotech/redo-web/flowchart";
import { AnimatedSkeletonBlock } from "@redotech/redo-web/skeleton";
import { Text } from "@redotech/redo-web/text";
import { assertNever } from "@redotech/util/type";
import * as resetModalCss from "./reset-modal.module.css";
import {
  flowTypeToText,
  IconType,
  Page,
  ResourceResetType,
  useResetModal,
} from "./use-reset-modal";

export const ResetModal = ({
  open,
  onClose,
  onSubmitFinished,
  resource,
}: {
  open: boolean;
  onClose: () => void;
  onSubmitFinished: () => void;
  resource: ResourceResetType;
}) => {
  const {
    icon,
    texts,
    resetReason: rejectionReason,
    resetReasonDisabled,
    onResetReasonChange: onRejectionReasonChange,
    onGoToChangeResetPointPage,
    onGotoChangeResetPointPageDisabled,
    onContinue,
    onCancel,
    page,
    flowchartProps,
    flowTypeProps,
    continueButtonDisabled,
    cancelButtonDisabled,
    loading,
    setChangeDefaultStepSelection,
    changeDefaultStepSelection,
    linkToReturnPageLoad,
    openReturnDetails,
  } = useResetModal({ resource, onSubmitFinished });

  return (
    <RedoModal
      buttonPlacement="full"
      isOpen={open}
      onModalCloseRequested={onClose}
      primaryButton={{
        text: texts.continueButton,
        disabled: continueButtonDisabled,
        onClick: onContinue,
      }}
      secondaryButton={{
        text: texts.cancelButton,
        disabled: cancelButtonDisabled,
        onClick: onCancel,
      }}
      subtitle={texts.subtitle}
      theme={(() => {
        switch (page) {
          case Page.CONFIRM:
            return "success";
          case Page.CHANGE_RESET_POINT:
            return "neutral";
          default:
            assertNever(page);
        }
      })()}
      title={texts.title}
      TitleIcon={() => {
        switch (icon) {
          case IconType.CHECK_CIRCLE:
            return <CheckCircle />;
          case IconType.REFRESH_CCW:
            return <RefreshCcw />;
          default:
            assertNever(icon);
        }
      }}
    >
      {(() => {
        switch (page) {
          case Page.CONFIRM:
            return (
              <Flex dir="column" gap="xl">
                {loading ? (
                  <Flex dir="column" gap="sm">
                    <AnimatedSkeletonBlock height="20px" width="80px" />
                    <AnimatedSkeletonBlock height="40px" width="100%" />
                  </Flex>
                ) : !onGotoChangeResetPointPageDisabled ? (
                  <Flex flexDirection="column" gap="sm">
                    {flowTypeProps && (
                      <RedoSingleSelectDropdownInput
                        label="Flow type"
                        options={flowTypeProps.items.map((item) => ({
                          id: item,
                          value: item,
                          type: "text",
                          text: flowTypeToText[item],
                        }))}
                        optionSelected={function (args): void {
                          flowTypeProps.setSelectedItem(args.item.value);
                        }}
                        selectedItem={{
                          id: flowTypeProps.flowType,
                          value: flowTypeProps.flowType,
                          type: "text",
                          text: flowTypeToText[flowTypeProps.flowType],
                        }}
                      />
                    )}
                    <RedoLinkInput
                      icon={LinkExternal2}
                      label={texts.resetPointLabel}
                      onClick={onGoToChangeResetPointPage}
                      placeholder={texts.resetPointPlaceholder}
                      size="md"
                      value={texts.resetPointText}
                    />
                  </Flex>
                ) : null}
                {linkToReturnPageLoad.value ? (
                  <Flex
                    align="center"
                    flexDirection="column"
                    gap="sm"
                    justify="center"
                  >
                    <Text fontSize="sm">{texts.linkToReturnPageText}</Text>
                    <RedoButton
                      hierarchy="link_underline"
                      onClick={openReturnDetails}
                      text="Open return page"
                    />
                  </Flex>
                ) : null}
                <RedoTextareaInputField
                  containerHeight="144px"
                  disabled={resetReasonDisabled}
                  label={texts.resetReasonLabel}
                  placeholder={texts.resetReasonPlaceholder}
                  setValue={onRejectionReasonChange}
                  size={RedoTextareaInputFieldSize.REGULAR}
                  value={rejectionReason}
                />
              </Flex>
            );
          case Page.CHANGE_RESET_POINT:
            return (
              <Flex dir="column" gap="2xl">
                <div className={resetModalCss.flowchartContainer}>
                  <Flowchart {...flowchartProps} showBorder />
                </div>

                <RedoRadioGroup
                  label="Change default step"
                  options={[
                    {
                      label: "Change reset point for this return only",
                      id: false,
                    },
                    {
                      label:
                        "Change reset point for this and all future returns",
                      id: true,
                    },
                  ]}
                  setValue={(value: boolean) =>
                    setChangeDefaultStepSelection(value)
                  }
                  value={changeDefaultStepSelection}
                />
              </Flex>
            );

          default:
            assertNever(page);
        }
      })()}
    </RedoModal>
  );
};
